import React, { useState, useCallback, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SiteConfigProvider, useSiteConfig } from "./context/SiteConfigContext";
import { AiAccessProvider } from "./hooks/useAiAccess";
import { BillingProvider } from "./hooks/useBilling";
import { EntitlementsProvider } from "./hooks/useEntitlements";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import { ReadOnlyGraceBanner } from "./components/ReadOnlyGraceBanner";
// v5.8 / Onda 9.R — QuotaExceededBanner removed (replaced by QuotaExceededPaywall modal)
// import { QuotaExceededBanner } from "./components/QuotaExceededBanner";
import { BillingStatusBanner } from "./components/BillingStatusBanner";
import ModuleAccessPaywall from "./components/ModuleAccessPaywall";
import QuotaExceededPaywall from "./components/QuotaExceededPaywall";

// Pages
// L1 «Sito leggero» (29/10/2026): le pagine pubbliche escono dal bundle
// iniziale. Stesso Suspense delle altre 115 rotte (AppRoutes); il guscio
// SEO e' server-side, quindi per Google non cambia nulla. I chunk con lo
// stesso webpackChunkName si fondono (pagine sorelle = un file solo).
const ForgotPasswordPage = lazy(() => import(/* webpackChunkName: "auth" */ "./pages/AuthPages").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import(/* webpackChunkName: "auth" */ "./pages/AuthPages").then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import(/* webpackChunkName: "auth" */ "./pages/AuthPages").then(m => ({ default: m.VerifyEmailPage })));
// Onda 28 — blocking page for authenticated users who haven't verified
// their email yet. Wrapped by RequireAuthOnly (defined in this file).
const VerifyEmailRequiredPage = lazy(() => import("./features/auth/VerifyEmailRequiredPage"));
const PrivacyPolicyPage = lazy(() => import(/* webpackChunkName: "legale" */ "./pages/PrivacyPolicyPage"));   // L1
const BlogIndexPage = lazy(() => import("./features/storefront/BlogIndexPage"));
const BlogArticlePage = lazy(() => import("./features/storefront/BlogArticlePage"));
const HowItWorksPage = lazy(() => import("./features/storefront/HowItWorksPage"));
const TermsOfServicePage = lazy(() => import(/* webpackChunkName: "legale" */ "./pages/TermsOfServicePage"));   // L1
const SubProcessorsPage = lazy(() => import(/* webpackChunkName: "legale" */ "./pages/SubProcessorsPage"));   // L1
// Wave GDPR-Commerce Piece 1b (2026-05-19) — tokenised marketing unsubscribe page
const MarketingUnsubscribePage = lazy(() => import(/* webpackChunkName: "legale" */ "./pages/MarketingUnsubscribePage"));   // L1
// Wave GDPR-Commerce CG-2 — public per-store legal pages
const StorefrontPrivacyPage = lazy(() => import(/* webpackChunkName: "legale" */ "./pages/StorefrontLegalPage").then(m => ({ default: m.StorefrontPrivacyPage })));   // L1
const StorefrontTermsPage = lazy(() => import(/* webpackChunkName: "legale" */ "./pages/StorefrontLegalPage").then(m => ({ default: m.StorefrontTermsPage })));   // L1
// Wave GDPR-Commerce CG-4 — blocking customer re-consent modal
import CustomerReconsentModal from "./features/customer-portal/CustomerReconsentModal";
// Wave GDPR-Admin Phase E — re-consent modal + cookie disclosure
import ReconsentModal from "./components/legal/ReconsentModal";
import SpazioRedirect from "./components/SpazioRedirect";
import CookieConsentBanner from "./components/legal/CookieConsentBanner";
import { trackPageView } from "./lib/analytics";
import { metaPageView } from "./lib/meta";   // MP2: PageView di Meta sui cambi rotta, solo col consenso
const ModulesPage = lazy(() => import("./pages/ModulesPage"));

// Features
const DashboardPage = lazy(() => import("./features/dashboard/DashboardPage"));
// CustomersLightPage legacy removed during Phase-3 single-brain
// consolidation. Its routes are served by CustomerInsightsPage.
const CustomerInsightsPage = lazy(() => import("./features/customer-insights/CustomerInsightsPage"));
const ProductPerformancePage = lazy(() => import("./features/product-catalog/ProductPerformancePage"));
const ProductsPage = lazy(() => import("./features/products/ProductsPage"));
const CheckInPage = lazy(() => import("./features/events/CheckInPage"));
const EventDashboardPage = lazy(() => import("./features/events/EventDashboardPage"));
const EventsListPage = lazy(() => import("./features/events/EventsListPage"));
const EventWizard = lazy(() => import("./features/events/EventWizard"));
// L1 «Sito leggero» (29/10/2026): le pagine della rete escono dal bundle
// iniziale. Profilo ed evento hanno webpackPrefetch (D5): il browser li
// scarica in idle dopo il caricamento, cosi' dal primo schermo si aprono
// subito; chi atterra su /cerca-ritiro non paga nulla in piu' all'inizio.
const RetreatsCalendarPage = lazy(() => import(/* webpackChunkName: "calendario" */ "./features/storefront/RetreatsCalendarPage"));
const OperatorProfilePage = lazy(() => import(/* webpackChunkName: "profilo", webpackPrefetch: true */ "./features/storefront/OperatorProfilePage"));
// LK2 — la pagina link per la bio di Instagram (/@slug e /l/slug)
const LinkPage = lazy(() => import(/* webpackChunkName: "profilo" */ "./features/storefront/LinkPage"));
// PV3 — l'intervista Aurya su pagina propria, in continuità col profilo
const OperatorInterviewPage = lazy(() => import(/* webpackChunkName: "profilo" */ "./features/storefront/OperatorInterviewPage"));
const OperatorsIndexPage = lazy(() => import(/* webpackChunkName: "operatori" */ "./features/storefront/OperatorsIndexPage"));
const DestinationsPage = lazy(() => import(/* webpackChunkName: "operatori" */ "./features/storefront/DestinationsPage"));
const ServiceWizard = lazy(() => import("./features/services/ServiceWizard"));
const ListinoPage = lazy(() => import("./features/listino/ListinoPage"));
// P1 (6/10/2026) — prodotti fisici e digitali venduti dal profilo
const ProdottiPage = lazy(() => import("./features/prodotti/ProdottiPage"));
const ProdottoDigitaleWizard = lazy(() => import("./features/prodotti/ProdottoDigitaleWizard"));
const ProdottoPage = lazy(() => import("./features/prodotti/ProdottoPage"));
const ProdottoFisicoWizard = lazy(() => import("./features/prodotti/ProdottoFisicoWizard"));
// 6/10 sera (founder): i Prodotti escono in ANTEPRIMA finche' non arriva il
// via — la scheda in Strumenti si vede, le pagine no (tornano a Strumenti).
import { prodottiAperti } from "./features/prodotti/stato";
// AC1 (7/10/2026): l'Accademia in ANTEPRIMA, stesso cancello dei Prodotti
import { accademiaAperta } from "./features/accademia/stato";
const AccademiaPage = lazy(() => import("./features/accademia/AccademiaPage"));
const CorsoWizard = lazy(() => import("./features/accademia/CorsoWizard"));
const CorsoPage = lazy(() => import("./features/accademia/CorsoPage"));
const CorsoStudentePage = lazy(() => import("./features/account/CorsoStudentePage"));   // AC2: il player dello studente
const AccademiaGate = ({ children }) => {
  const { user } = useAuth();
  return accademiaAperta(user) ? children : <Navigate to="/strumenti" replace />;
};
const ProdottiGate = ({ children }) => {
  const { user } = useAuth();   // dentro ProtectedRoute: l'utente c'e'
  return prodottiAperti(user) ? children : <Navigate to="/strumenti" replace />;
};
const ReservationWizard = lazy(() => import("./features/reservations/ReservationWizard"));
const PhysicalWizard = lazy(() => import("./features/physicals/PhysicalWizard"));
const PhysicalDashboardPage = lazy(() => import("./features/physicals/PhysicalDashboardPage"));
const DigitalWizard = lazy(() => import("./features/digitals/DigitalWizard"));
const DigitalDashboardPage = lazy(() => import("./features/digitals/DigitalDashboardPage"));
// Release 4 (Courses) Step 2 — admin UI for video courses
// AC3 (7/10/2026): l'editor corsi legacy (/courses) e' dismesso → /accademia
const ReservationsDashboard = lazy(() => import("./features/reservations/ReservationsDashboard"));
const ReservationDashboardPage = lazy(() => import("./features/reservations/ReservationDashboardPage"));
const ServiceDashboardPage = lazy(() => import("./features/services/ServiceDashboardPage"));
const TicketsManagementPage = lazy(() => import("./features/events/TicketsManagementPage"));
const CustomersMgmtPage = lazy(() => import("./features/customers-mgmt/CustomersMgmtPage"));
const SuppliersPage = lazy(() => import("./features/suppliers/SuppliersPage"));
const OrdersPage = lazy(() => import("./features/orders/OrdersPage"));
const CalendarPage = lazy(() => import("./features/calendar/CalendarPage"));
const CouponsPage = lazy(() => import("./features/coupons/CouponsPage"));
const DataIntegrityPage = lazy(() => import("./features/data-integrity/DataIntegrityPage"));
// PS3 — la vecchia pagina impostazioni store non e' piu' raggiungibile:
// la rotta /store-settings (mai linkata, autodichiarata deprecata) ora
// redirige a /settings. Il file resta nel repo per il mondo legacy.
// Wave GDPR-Commerce CG-7 — admin DPA page
const DpaPage = lazy(() => import("./pages/DpaPage"));
// SetupPage removed in Fase 2 Track F Step 9 (replaced by the dynamic
// dashboard SetupWizardWidget in features/setup-wizard/). The /setup
// route below now redirects to /dashboard for backward-compat with any
// stale links/emails pointing at /setup.
const StoresPage = lazy(() => import("./features/stores/StoresPage"));
const NewsletterPage = lazy(() => import("./features/newsletter/NewsletterPage"));
const ReviewsAdminPage = lazy(() => import("./features/reviews/ReviewsAdminPage"));
const IncassiPage = lazy(() => import("./features/cashflow/IncassiPage"));
const VisibilityPage = lazy(() => import("./features/visibility/VisibilityPage"));
const OperatorLandingPage = lazy(() => import("./features/prelaunch/OperatorLandingPage"));
const OperatorDisciplineLandingPage = lazy(() => import("./features/prelaunch/OperatorDisciplineLandingPage"));   // L3 (26/10/2026)
const PiattaformaPage = lazy(() => import("./features/prelaunch/PiattaformaPage"));   // PP (3/11/2026): la piattaforma pezzo per pezzo
const EsperienzePeriodoPage = lazy(() => import("./features/storefront/EsperienzePeriodoPage"));   // SC-B (27/10/2026)
// RD — il secondo tempo della registrazione diretta (campi nel profilo)
const WelcomeRetePage = lazy(() => import("./features/prelaunch/WelcomeRetePage"));
const PricingPage = lazy(() => import("./features/prelaunch/PricingPage"));
// RT1 (luglio) mandava /cerca-ritiro su /newsletter: «il pubblico
// viaggiatori si coltiva in newsletter». RB4 (10/9/2026): dal 4/8 zero
// contatti «cerco un ritiro» — la porta torna, si chiama con l'oggetto
// («Trovami il mio ritiro») e ISCRIVE al Cerchio con i ritiri accesi.
const TravelerLandingPage = lazy(() => import("./features/prelaunch/TravelerLandingPage"));
const AziendePage = lazy(() => import("./features/network/AziendePage"));   // P13 — il team building
const NewsletterLandingPage = lazy(() => import("./features/prelaunch/NewsletterLandingPage"));
const NewsletterConfirmPage = lazy(() => import("./features/prelaunch/NewsletterConfirmPage"));
const NewsletterPreferencesPage = lazy(() => import("./features/prelaunch/NewsletterPreferencesPage"));
// RT2 — le pagine della fase rete
const NetworkHomePage = lazy(() => import("./features/network/NetworkHomePage"));
const ManifestoPage = lazy(() => import("./features/network/ManifestoPage"));
// SW3 — Chi siamo torna una pagina propria: il Manifesto e' la
// posizione, Chi siamo sono le persone (sostituisce AboutAuryaPage,
// rimossa dal repo con la sua voce vecchia).
const ChiSiamoPage = lazy(() => import("./features/network/ChiSiamoPage"));
const CashflowDataPage = lazy(() => import("./features/cashflow/CashflowDataPage"));
const PosPage = lazy(() => import("./features/stores/PosPage"));
const StorefrontPage = lazy(() => import("./features/storefront/StorefrontPage"));   // SEO-E: fuori dal bundle iniziale
const EventLandingPage = lazy(() => import(/* webpackChunkName: "evento", webpackPrefetch: true */ "./features/storefront/EventLandingPage"));   // L1 (29/10): fuori dal bundle iniziale, prefetch in idle
const TicketLandingPage = lazy(() => import("./features/storefront/TicketLandingPage"));   // SEO-E: fuori dal bundle iniziale
const AccountLoginPage = lazy(() => import("./features/account/AccountLoginPage"));   // SEO-E: fuori dal bundle iniziale
const AccountVerifyEmailPage = lazy(() => import("./features/account/AccountVerifyEmailPage"));   // SEO-E: fuori dal bundle iniziale
const AccountResetPasswordPage = lazy(() => import("./features/account/AccountResetPasswordPage"));   // SEO-E: fuori dal bundle iniziale
const AccountPage = lazy(() => import("./features/account/AccountPage"));   // SEO-E: fuori dal bundle iniziale
const BookingLandingPage = lazy(() => import("./features/storefront/BookingLandingPage"));   // SEO-E: fuori dal bundle iniziale
const ReservationConfirmationPage = lazy(() => import("./features/storefront/ReservationConfirmationPage"));   // SEO-E: fuori dal bundle iniziale
const RimandoLandingServizio = lazy(() => import("./features/storefront/RimandoLandingServizio"));   // SS (27/10/2026): /p/ rimanda al profilo
const ProdottoLandingPage = lazy(() => import("./features/storefront/ProdottoLandingPage"));   // DP (6/10): la pagina del prodotto
const CorsoLandingPage = lazy(() => import("./features/storefront/CorsoLandingPage"));         // AC3 (7/10): la pagina del corso
const CorsiDirectoryPage = lazy(() => import("./features/storefront/CorsiDirectoryPage"));     // RF (8/10): la directory dei corsi
const ReservationLandingPage = lazy(() => import("./features/storefront/ReservationLandingPage"));   // SEO-E: fuori dal bundle iniziale
const PhysicalLandingPage = lazy(() => import("./features/storefront/PhysicalLandingPage"));   // SEO-E: fuori dal bundle iniziale
const DigitalLandingPage = lazy(() => import("./features/storefront/DigitalLandingPage"));   // SEO-E: fuori dal bundle iniziale
// Step 4 of the language-system refactor: wraps every public storefront
// surface with StoreMetaProvider + locale sync so the i18n resolver has
// the merchant's allowed-languages list available on first render.
import PublicStorefrontShell from "./features/storefront/PublicStorefrontShell";
const DownloadLandingPage = lazy(() => import("./features/storefront/DownloadLandingPage"));   // SEO-E: fuori dal bundle iniziale
// SEO-E (14/9 sera): le pagine del checkout escono dal bundle iniziale
const CheckoutSuccessPage = lazy(() => import("./features/storefront/CheckoutResultPage").then(m => ({ default: m.CheckoutSuccessPage })));
const CheckoutCancelPage = lazy(() => import("./features/storefront/CheckoutResultPage").then(m => ({ default: m.CheckoutCancelPage })));
const PayLinkUnavailablePage = lazy(() => import("./features/storefront/CheckoutResultPage").then(m => ({ default: m.PayLinkUnavailablePage })));
const TeamPage = lazy(() => import("./features/team/TeamPage"));
const SettingsPage = lazy(() => import("./features/settings/SettingsPage"));
const PublicProfilePage = lazy(() => import("./features/settings/PublicProfilePage"));
const SchedaBibliotecaPage = lazy(() => import("./features/frequenze/SchedaBibliotecaPage"));
// FQ0 — Frequenze by Aurya: compositore tracce vibrazionali (lazy: il
// motore di sintesi e l'encoder MP3 vivono solo nel suo chunk)
const FrequenzePage = lazy(() => import("./features/frequenze/FrequenzePage"));
const AiCreaPage = lazy(() => import("./features/frequenze/ai/AiCreaPage"));   // AI2 (9/10/2026)
const PublicFrequencyPage = lazy(() => import("./features/frequenze/PublicFrequencyPage"));
const MeditazioniPage = lazy(() => import("./features/frequenze/MeditazioniPage"));
const PlaylistPage = lazy(() => import("./features/frequenze/casa/PlaylistPage"));   // SN1 — la playlist
const PiuPage = lazy(() => import("./features/frequenze/casa/PiuPage"));             // SN4 — Aurya Più (rimanda finché è spento)
import { SOUND_ESPLORA_NUOVA } from "./features/frequenze/stato";   // ES0
const SoundHomePage = lazy(() => import("./features/frequenze/SoundHomePage"));
const BibliotecaPage = lazy(() => import("./features/frequenze/esplora/BibliotecaPage"));   // ES0 — le frequenze
const FondamentaPage = lazy(() => import("./features/frequenze/esplora/FondamentaPage"));   // ES0 — le fondamenta
const VisualPage = lazy(() => import("./features/frequenze/visual/VisualPage"));
/* LU (28/8) — il Lab e' una casa con le stanze: la Sala accoglie,
   ogni stanza ha il suo indirizzo (condivisibile, con la sua SEO). */
const LabSala = lazy(() => import("./features/frequenze/lab/LabSala"));
const LabBanco = lazy(() => import("./features/frequenze/lab/LabBanco"));
const LabOrecchio = lazy(() => import("./features/frequenze/lab/LabOrecchio"));
const LabRitratto = lazy(() => import("./features/frequenze/lab/LabRitratto"));
const LabMeraviglie = lazy(() => import("./features/frequenze/lab/LabMeraviglie"));
const LabRisonanze = lazy(() => import("./features/frequenze/lab/LabRisonanze"));
const EsperienzaPage = lazy(() => import("./features/frequenze/esperienze/EsperienzaPage"));
const SoundProPage = lazy(() => import("./features/frequenze/pro/SoundProPage"));
const ProfessionalLanding = lazy(() => import("./features/frequenze/ProfessionalLanding"));
const CreaStudioLanding = lazy(() => import("./features/frequenze/CreaStudioLanding"));
const AscoltaPage = lazy(() => import("./features/frequenze/AscoltaPage"));
// PL17 — lazy come tutte le pagine admin: da eager trascinava Layout
// (e con lui TUTTE le traduzioni back-office) nel bundle pubblico.
const IniziaPage = lazy(() => import("./features/onboarding/IniziaPage"));
const AdminPage = lazy(() => import("./features/admin/AdminPage"));
const SoundAccessPage = lazy(() => import("./features/admin/SoundAccessPage"));
const ConcessioniPage = lazy(() => import("./features/admin/ConcessioniPage"));   // M6 (22/10/2026)
// SA-R (10/9/2026 sera) — le pagine del menu System
const CerchioPage = lazy(() => import("./features/admin/CerchioPage"));
const OperatoriPage = lazy(() => import("./features/admin/OperatoriPage"));
const MagazinePage = lazy(() => import("./features/admin/MagazinePage"));
const TecnicoPage = lazy(() => import("./features/admin/TecnicoPage"));
const StrutturePage = lazy(() => import("./features/admin/strutture/StrutturePage"));
const StrutturaScheda = lazy(() => import("./features/admin/strutture/StrutturaScheda"));
const PlansPage = lazy(() => import("./pages/PlansPage"));
const StrumentiPage = lazy(() => import("./pages/StrumentiPage"));

// Customer Identity Foundation (v9.0)
import { CustomerAuthProvider } from "./context/CustomerAuthContext";
// AC3 (7/10/2026): CustomerProtectedRoute e CustomerLayout non servono piu' qui
// (il player legacy e' dismesso); i file restano per il portale clienti legacy.
// Auth pages — Phase 5 of the customer area refactor moved each one
// into a dedicated file under customer-portal/auth/. The shared
// AuthShell + useStoreInfo helpers live alongside.
// PS4 — restano SOLO le pagine strutturali per il player corsi legacy
// (login = target del gate CustomerProtectedRoute, recupero password e
// verifica email = link nelle email gia' spedite). La signup legacy e'
// rediretta: i nuovi utenti passano da /account/accedi (login Aurya).
const CustomerForgotPasswordPage = lazy(() => import(/* webpackChunkName: "auth-cliente" */ "./features/customer-portal/auth/ForgotPasswordPage"));   // L1
const CustomerResetPasswordPage = lazy(() => import(/* webpackChunkName: "auth-cliente" */ "./features/customer-portal/auth/ResetPasswordPage"));   // L1
const CustomerVerifyEmailPage = lazy(() => import(/* webpackChunkName: "auth-cliente" */ "./features/customer-portal/auth/VerifyEmailPage"));   // L1

// Customer area refactor — Phase 3: portal pages now mount inside
// <CustomerLayout> (TopBar + Sidebar + email banner shared chrome).
// The legacy CustomerPortalPage / CustomerOrderDetailPage / MyCoursesPage
// imports are intentionally NOT re-added here — their routes are now
// served by the new pages/* below. The old files stay in the codebase
// for the auth re-exports above (until Phase 6 turns them into a shim).
import { caricaDiscipline } from './lib/disciplines';   // DV3: registro vivo delle discipline
// PS4 — del portale clienti legacy restano SOLO i corsi (il player e
// il suo indice): le email "Vai al corso" gia' spedite puntano a
// /account/courses/<enrollment_id> e il player usa il JWT customer.
// Ordini e profilo legacy sono rediretti all'account Aurya (/account).
// AC3 (7/10/2026): il player legacy (/account/courses) e' dismesso → /account/corsi
const RedirectCorsoLegacy = () => { const { enrollment_id } = useParams(); return <Navigate to={`/account/corsi/${enrollment_id}`} replace />; };
const RedirectCoLegacy = () => { const { org_slug, product_slug } = useParams(); return <Navigate to={`/corso/${org_slug}/${product_slug}`} replace />; };

// Protected Route Component
//
// Onda 28 — Email-verification gate. Requires:
//   1. authenticated (token + user)
//   2. user.email_verified === true
// (system_admin role bypasses #2 — operational continuity.)
//
// If authenticated but not yet verified, redirect to
// /verify-email-required, which is wrapped in RequireAuthOnly below
// (= the only protected page accessible to unverified users).
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // LN0 — il login riporta DOVE si stava andando, non in /dashboard:
    // e' il rimbalzo che al refresh faceva perdere la pagina.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  // Onda 28 — email verification gate. Strictly compare to false so
  // that legacy /me responses without the field (or transient race
  // conditions during context load) don't false-positive a redirect.
  // E6 (24/9/2026) — con `verifica_morbida` dal server si entra lo
  // stesso: l'avviso in cima al gestionale (BannerVerificaEmail) fa
  // il resto, e i cancelli rigidi restano lato server.
  if (user && user.role !== "system_admin" && user.email_verified === false && !user.verifica_morbida) {
    return <Navigate to="/verify-email-required" replace />;
  }

  return children;
};

// RequireAuthOnly — wraps the /verify-email-required page only.
// Lets through any authenticated user EXCEPT one who's already
// verified (or is a system_admin) — those get redirected straight to
// /dashboard, since the verification page is irrelevant for them.
//
// Onda 28: this is the inverse gate of ProtectedRoute — it accepts
// users that ProtectedRoute rejects, and rejects users that
// ProtectedRoute accepts. The two together cleanly partition the
// authenticated user space without overlap.
const RequireAuthOnly = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/accedi" replace />;
  }

  // Already verified (or system_admin) → no reason to stay on the
  // verification page; bounce to dashboard.
  if (user && (user.role === "system_admin" || user.email_verified !== false)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// System Admin Route — only accessible to users with role === "system_admin".
// Regular org users (admin / user) are silently redirected to /dashboard.
const SystemAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/accedi" replace />;
  }

  if (user.role !== "system_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// S0.1 — redirect che porta la query string con sé (Navigate 'to' string
// la perderebbe: i link /ritiri?categoria=yoga devono filtrare la home).
const RedirectPreservingQuery = ({ to }) => {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
};

// S6 — fallback dei chunk lazy del back-office: stesso spinner delle
// route guard. Le pagine PUBBLICHE restano nel bundle principale (sono
// la superficie SEO e devono idratare subito).
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // SP3 — chi e' gia' dentro e arriva qui con una destinazione
    // (es. «sei operatore?» da Aurya Sound) ci va DIRITTO, senza
    // passare dal form ne' atterrare in gestionale. Vale anche subito
    // dopo il login: questo redirect vinceva la corsa col navigate
    // della LoginPage, ed e' il motivo per cui il percorso finiva
    // sempre in /dashboard. Solo path interni (no open redirect).
    const next = new URLSearchParams(location.search).get('next') || '';
    const safe = next.startsWith('/') && !next.startsWith('//');
    return <Navigate to={safe ? next : '/dashboard'} replace />;
  }

  return children;
};

// RB3 — la SPA manteneva la posizione di scroll tra le pagine: chi
// navigava dal footer atterrava in FONDO alla pagina nuova. Al cambio
// di pathname si riparte dall'alto; i cambi di sola query (filtri,
// vista mappa) non scrollano, chi sta filtrando non va disturbato.
// HP2 — ...tranne quando l'URL porta un'ancora. Un link tipo
// /entra-nella-rete#presentati e' una destinazione precisa: mandarlo in
// cima alla pagina e' esattamente il bug che RB3 voleva risolvere, al
// contrario. Il router tiene la history in memoria, quindi il salto
// nativo del browser non avviene: lo facciamo qui, per TUTTE le ancore
// del sito e non caso per caso.
// Il bersaglio puo' arrivare in ritardo: le pagine sono lazy, quindi al
// commit della rotta l'ancora spesso non esiste ancora. Si riprova a
// intervalli per un paio di secondi, poi si smette in silenzio.
// Timer e non requestAnimationFrame: rAF viene sospeso quando la scheda
// non e' in primo piano, e chi apre un link con ancora in una scheda di
// sfondo la troverebbe in cima invece che sull'ancora.
// Salto sempre istantaneo: un'ancora si comporta come un'ancora, e
// nessuno vuole vedersi scorrere sotto gli occhi mezza pagina che non ha
// chiesto di vedere.
const ANCHOR_RETRY_MS = 60;
const ANCHOR_GIVE_UP_MS = 2000;

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  // RF-ter (8/10/2026, founder: «clicco Comincia e atterro nel footer»):
  // un link con un'ancora che NON e' un elemento (es. #lesson-<id>, che il
  // player usa solo per scegliere la lezione) lasciava la pagina nuova allo
  // scroll della pagina vecchia. Ora al CAMBIO di pagina si torna sempre in
  // cima prima di cercare l'ancora; se cambia solo l'hash sulla stessa
  // pagina (schede del profilo, #listino) non si tocca nulla.
  const ultimoPathname = React.useRef(null);
  React.useEffect(() => {
    const paginaNuova = ultimoPathname.current !== pathname;
    ultimoPathname.current = pathname;
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    if (paginaNuova) window.scrollTo(0, 0);
    let timer = 0;
    let waited = 0;
    const seek = () => {
      let el = null;
      // un hash qualsiasi (#, #!, caratteri strani) non deve far
      // esplodere querySelector e portarsi via la navigazione
      try { el = document.querySelector(hash); } catch { el = null; }
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
      waited += ANCHOR_RETRY_MS;
      if (waited < ANCHOR_GIVE_UP_MS) timer = window.setTimeout(seek, ANCHOR_RETRY_MS);
    };
    seek();
    return () => window.clearTimeout(timer);
  }, [pathname, hash]);
  return null;
}

// PL4→RT2 — la home segue la fase: network = la home della rete
// (manifesto, Magazine, candidatura, newsletter), marketplace = la
// directory dei ritiri. Per TUTTI, anche l'admin loggato: vedi sempre
// cio' che vede il pubblico. Il back-office resta su /dashboard.
function HomeGate() {
  const { sitePhase, loading } = useSiteConfig();
  if (loading) return null;
  if (sitePhase === 'network') return <NetworkHomePage />;
  return <RetreatsCalendarPage />;
}

// RT1 (piano sito-rete) — in fase network la vetrina ritiri NON esiste
// per il visitatore: niente anteprima di campioni, il valore lo danno
// Magazine, Manifesto e (RT3) i profili della rete. In fase marketplace
// resta il redirect S0 alla home (la home È la directory).
// RE (10/9/2026 sera, founder: «riaccendiamo»): /esperienze E' il
// calendario dei ritiri (RetreatsCalendarPage), in ogni fase. La pagina
// semplice di P3 (EsperienzePage) e' stata ritirata: una pagina sola.
function EsperienzeGate() {
  return <RetreatsCalendarPage />;
}

function RitiriGate() {
  // /ritiri → /esperienze (nginx fa il 301 vero; qui vale per la SPA)
  return <RedirectPreservingQuery to="/esperienze" />;
}

// /esplora-ritiri era l'anteprima non linkata (29/7): ora e' /esperienze
function EsploraRitiriRedirect() {
  const { categoria, regione } = useParams();
  const dove = '/esperienze' + (categoria ? `/${categoria}` : '') + (regione ? `/${regione}` : '');
  return <RedirectPreservingQuery to={dove} />;
}

// PP2 — le pagine categoria seguono la stessa regola di /ritiri: in
// fase network la vetrina non esiste (i campioni sfocati che restavano
// qui erano un residuo dell'era prelaunch), in marketplace la
// categoria e' una pagina vera.
function RitiriCategoryGate() {
  // RE (10/9): le pagine categoria/regione vivono sotto /esperienze
  const { categoria, regione } = useParams();
  const dove = '/esperienze' + (categoria ? `/${categoria}` : '') + (regione ? `/${regione}` : '');
  return <RedirectPreservingQuery to={dove} />;
}

// PL23→RT3 — in fase network /operatori era la landing della rete
// (NetworkOperatorsPage: il racconto, «presto potrai conoscere...»).
// SR1 (3/9/2026, piano SITO IMMEDIATO): con 8 professionisti veri
// pubblicati, /operatori E' la directory in ogni fase — le persone
// prima delle parole. NetworkOperatorsPage resta nel codice, spenta.
// Il gate resta come punto unico di decisione (guardie PL).
function OperatorsGate() {
  const { loading } = useSiteConfig();
  if (loading) return null;
  return <OperatorsIndexPage />;
}

// SR1 — /esplora-operatori era l'anteprima non linkata della directory:
// ora la directory vive su /operatori, la vecchia rotta rimanda li'
// (categoria compresa) cosi' nessun link o segnalibro muore.
function EsploraOperatoriRedirect() {
  const { categoria } = useParams();
  return <Navigate to={categoria ? `/operatori/${categoria}` : '/operatori'} replace />;
}

function DestinationsGate() {
  const { sitePhase, loading } = useSiteConfig();
  if (loading) return null;
  if (sitePhase === 'network') return <Navigate to="/" replace />;
  return <DestinationsPage />;
}

// LK2 — il catch-all riconosce gli handle /@slug della pagina link
// (React Router non accetta il prefisso @ dentro un segmento di
// rotta). Tutto il resto continua a tornare alla home, come prima.
function CatchAllOrHandle() {
  const { pathname } = useLocation();
  const m = pathname.match(/^\/@([^/]+)\/?$/);
  if (m) return <LinkPage handle={m[1]} />;
  return <Navigate to="/" replace />;
}

// LC2 — /come-funziona racconta il percorso d'acquisto (caparra,
// prenotazione, recensione): in fase network quel percorso non esiste
// e la pagina prometteva un marketplace spento. Il posto dove si
// spiega "come funziona Aurya" oggi e' il Manifesto.
function HowItWorksGate() {
  const { sitePhase, loading } = useSiteConfig();
  if (loading) return null;
  if (sitePhase === 'network') return <Navigate to="/manifesto" replace />;
  return <HowItWorksPage />;
}

// GA1 — una page_view per ogni navigazione SPA (config con
// send_page_view: false, quindi niente doppi conteggi).
// PS6.4 — oltre al ping analytics, si tiene traccia della ROTTA DI
// PROVENIENZA in sessionStorage (aurya:nav:prev): serve alle superfici
// di checkout per derivare il canale dell'ordine dalla superficie
// reale (profilo vs marketplace). Solo pathname; il refresh non
// sovrascrive prev (cur non cambia), quindi la provenienza sopravvive
// a un F5 sulla landing.
function AnalyticsPageViews() {
  const location = useLocation();
  React.useEffect(() => {
    trackPageView(location.pathname + location.search);
    metaPageView();   // MP2 — no-op senza pixel o senza consenso marketing
    try {
      const cur = sessionStorage.getItem('aurya:nav:cur');
      if (cur !== location.pathname) {
        if (cur) sessionStorage.setItem('aurya:nav:prev', cur);
        sessionStorage.setItem('aurya:nav:cur', location.pathname);
      }
    } catch { /* storage inaccessibile: nessuna provenienza */ }
  }, [location.pathname, location.search]);
  return null;
}

// TW3 (piano Listino) — la vetrina /s/{slug} e' migrata sul profilo
// /o/{slug}: redirect SPA (gli URL non muoiono mai, invariante I6).
// Le pagine legal (/s/x/privacy, /terms) e checkout restano vive.
// RS3 fix — /s/x?checkout=1 e' l'handoff del checkout dalle landing
// /e/ e /p/: DEVE aprire il modale sullo storefront, non redirigere
// al profilo (I2, I3: il motore di acquisto non si tocca).
// PS6.1 — il consenso al checkout va LATCHATO al mount: StorefrontPage
// consuma ?checkout=1 (strip del param con navigate replace) e azzera il
// Router state dopo l'idratazione; senza latch questa guardia rivaluterebbe
// wantsCheckout=false e rimbalzerebbe al profilo A MODALE APERTO.
function StoreToProfileRedirect() {
  const { slug } = useParams();
  const location = useLocation();
  const [grant] = React.useState(() =>
    new URLSearchParams(location.search).has('checkout')
    || Boolean(location.state?.preloadCart));
  if (grant) return <StorefrontPage />;
  return <Navigate to={`/o/${slug}`} replace />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <AnalyticsPageViews />
    <Routes>
      {/* S0.1 — la ROOT è il marketplace: la pagina con l'autorità
          del dominio è la directory pubblica, non la login operatori.
          Chi è loggato vede comunque la home pubblica (il back-office
          si raggiunge dal menu / da /dashboard). */}
      <Route path="/" element={<HomeGate />} />
      {/* RT1 (piano sito-rete) — la sitemap della fase rete. Gli URL
          nuovi nascono ORA e non cambieranno mai; i contenuti si
          riempiono nelle onde successive (RT2 manifesto+home, RT3
          rete, RT4 newsletter). I vecchi URL redirigono. */}
      <Route path="/manifesto" element={<ManifestoPage />} />
      <Route path="/entra-nella-rete" element={<OperatorLandingPage />} />
      <Route path="/entra-nella-rete/:disciplina" element={<OperatorDisciplineLandingPage />} />   {/* L3: la landing per disciplina */}
      <Route path="/piattaforma" element={<PiattaformaPage />} />                          {/* PP: la piattaforma, madre */}
      <Route path="/piattaforma/:slug" element={<PiattaformaPage />} />                    {/* PP: gestionale, servizi, ritiri, accademia, negozio */}
      {/* RD — benvenuto post-registrazione: facoltativo, saltabile,
          scrive sul profilo esistente e prosegue su /inizia */}
      <Route
        path="/benvenuto"
        element={
          <ProtectedRoute>
            <WelcomeRetePage />
          </ProtectedRoute>
        }
      />
      {/* AB3 — i piani spiegati per esteso, linkata dalla FAQ "Quanto costa?" */}
      <Route path="/costi" element={<PricingPage />} />
      {/* 25/9 (founder) — l'indirizzo da dire a voce: dentro → gestionale, fuori → accesso */}
      <Route path="/spazio" element={<SpazioRedirect />} />
      <Route path="/newsletter" element={<NewsletterLandingPage />} />
      {/* BN2 — pagine token del double opt-in (noindex, servite 200 dalla shell) */}
      <Route path="/newsletter/conferma/:token" element={<NewsletterConfirmPage />} />
      <Route path="/newsletter/preferenze/:token" element={<NewsletterPreferencesPage />} />
      {/* HP2 — /magazine e' il nome che il brand usa a voce (header,
          footer, home). La rotta CANONICA resta /blog: e' quella in
          sitemap, gia' indicizzata e spinta con IndexNow, e non si
          rinomina per una questione di lessico. /magazine esiste come
          alias cosi' chi lo digita o lo trova scritto arriva a casa.
          Promuovere /magazine a canonica e' una decisione SEO a se':
          servirebbero sitemap, 301 lato server e un nuovo IndexNow. */}
      <Route path="/magazine" element={<Navigate to="/blog" replace />} />
      {/* HP5 — stessa storia di /magazine: "la rete" e' il nome che il
          brand usa a voce, /operatori e' l'indirizzo canonico gia' in
          sitemap. L'alias esiste perche' chi digita /rete deve arrivare
          a casa, non su un 404. */}
      <Route path="/rete" element={<Navigate to="/operatori" replace />} />
      {/* SW3 — /chi-siamo e' una pagina vera, non piu' un redirect sul
          Manifesto: il footer di fase rete la linka e chi ci clicca
          deve trovare le persone, non la posizione. */}
      <Route path="/chi-siamo" element={<ChiSiamoPage />} />
      {/* redirect permanenti dei vecchi percorsi */}
      <Route path="/per-operatori" element={<Navigate to="/entra-nella-rete" replace />} />
      <Route path="/cerca-ritiro" element={<TravelerLandingPage />} />
      <Route path="/aziende" element={<AziendePage />} />
      {/* ID (20/8) — LA PORTA UNICA. Una email, una password: il server
          decide il mondo (operatore/cliente) e col legame dei cappelli
          rilascia entrambi i token (SSO). /login e /account/accedi
          restano vivi per sempre come alias: i segnalibri non muoiono. */}
      <Route path="/accedi" element={<AccountLoginPage />} />
      <Route path="/login" element={<RedirectPreservingQuery to="/accedi" />} />
      <Route path="/come-funziona" element={<HowItWorksGate />} />
      {/* AN5 — il blog di Aurya */}
      {/* SEO1 (11/7, decisione founder): il blog è il motore SEO del
          pre-lancio e resta SEMPRE attivo, flag o non flag. */}
      <Route path="/blog" element={<BlogIndexPage />} />
      {/* BN5 — hub categoria indicizzabili (rotta vera, non query param) */}
      <Route path="/blog/categoria/:categoria" element={<BlogIndexPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      {/* Static legal pages — always accessible, no auth wrapper */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      {/* R5 — il footer marketplace punta a /termini (URL italiano):
          prima era un 404. Stessa pagina di /terms. */}
      <Route path="/termini" element={<TermsOfServicePage />} />
      {/* Wave GDPR-Admin Phase E — public sub-processor registry
          (GDPR Art. 28.3.i + 13.1.f). Discoverable independently from
          the full Privacy Policy text. */}
      <Route path="/legal/sub-processors" element={<SubProcessorsPage />} />
      {/* Wave GDPR-Commerce Piece 1b — public marketing-consent unsubscribe.
          Token in the path → no auth, no app chrome. The page handles
          its own brand header so the customer recognises afianco at the
          top while the call-to-action is about the merchant they're
          leaving (Art. 7(3) symmetry requirement). */}
      <Route path="/u/:token" element={<MarketingUnsubscribePage />} />
      {/* Public storefront — no auth, no layout
          --------------------------------------------------------------
          Each slug-bearing route is wrapped in <PublicStorefrontShell>
          so the i18n resolver and store branding work consistently
          across surfaces (Step 4 of the language-system refactor).

          Token-based routes (/t, /b, /d, /rsv) are intentionally NOT
          wrapped at this level: their slug is in the response payload,
          not the URL. Those landing pages mount their own
          <PublicStorefrontShell slug={data.store_slug}> after fetch. */}
      <Route path="/s/checkout-success" element={<CheckoutSuccessPage />} />
      <Route path="/s/checkout-cancel" element={<CheckoutCancelPage />} />
      {/* PS6.3 — atterraggio dei link /pay/{token} non piu' servibili:
          il backend reindirizza qui, mai piu' JSON nudo da email. */}
      <Route path="/s/pay-non-disponibile" element={<PayLinkUnavailablePage />} />
      {/* Wave GDPR-Commerce CG-2 — public per-store legal pages.
          The merchant edits docs in /settings/gdpr; here we serve the
          published version in their chosen display_locale (the same
          to all customers regardless of their UI language). */}
      <Route path="/s/:slug/privacy" element={<StorefrontPrivacyPage />} />
      <Route path="/s/:slug/terms" element={<StorefrontTermsPage />} />
      <Route path="/s/:slug" element={<StoreToProfileRedirect />} />
      {/* Phase 7.5 — per-category storefront page.
          Renders the SAME StorefrontPage component as `/s/:slug`; the
          page reads `useParams().category` and filters the product
          grid to a single item-type bucket. Cart state survives the
          remount because useStorefrontCart persists via sessionStorage
          (scoped by store slug — see hooks/useCartStorage.js). The
          root route `/s/:slug` redirects to the first non-empty
          category once the catalog loads, so visitors landing on
          the root URL get sent to a populated category page. */}
      <Route path="/s/:slug/c/:category" element={<StoreToProfileRedirect />} />
      {/* S3 — Chi siamo DENTRO il guscio store: stessa shell, stesso
          carrello; il contenuto e' il profilo pubblico. /o/:slug resta
          per il contesto directory. */}
      <Route path="/s/:slug/chi-siamo" element={<StoreToProfileRedirect />} />
      {/* E3: public event landing page — deep-link per-occurrence.
          Has StorefrontHeader → its inline switcher covers /e, no
          floating dup. */}
      {/* /ritiri → home: la directory È la home (S0.1). Redirect che
          PRESERVA la query (?categoria=... dai link footer/condivisi). */}
      <Route path="/ritiri" element={<RitiriGate />} />
      <Route path="/ritiri/:categoria" element={<RitiriCategoryGate />} />
      <Route path="/ritiri/:categoria/:regione" element={<RitiriCategoryGate />} />
      {/* S2 — aggregatori pubblici: organizzatori, destinazioni, esperienze */}
      <Route path="/operatori" element={<OperatorsGate />} />
      {/* LM3+ — anteprima pubblica del marketplace operatori, NON
          linkata nei menu (richiesta founder 29/7): stessa pagina
          della fase marketplace, raggiungibile solo via URL diretto */}
      <Route path="/esplora-operatori" element={<EsploraOperatoriRedirect />} />
      <Route path="/esplora-operatori/:categoria" element={<EsploraOperatoriRedirect />} />
      {/* PN (richiesta founder 29/7) — anteprima non linkata della
          directory ritiri: stessa pagina di /ritiri (marketplace) in
          OGNI fase, con dati veri via ?preview=1 e noindex. Nessuna
          voce di menu: si raggiunge solo via URL diretto. */}
      <Route path="/esplora-ritiri" element={<EsploraRitiriRedirect />} />
      <Route path="/esplora-ritiri/:categoria" element={<EsploraRitiriRedirect />} />
      <Route path="/esplora-ritiri/:categoria/:regione" element={<EsploraRitiriRedirect />} />
      <Route path="/operatori/:categoria" element={<OperatorsGate />} />
      {/* SEO-B (14/9 sera): /operatori/{disciplina}/{regione} — pagina locale */}
      <Route path="/operatori/:categoria/:sub" element={<OperatorsGate />} />
      <Route path="/destinazioni" element={<DestinationsGate />} />
      <Route path="/destinazioni/:luogo" element={<DestinationsGate />} />
      {/* DS3 (7/7) teneva /esperienze fuori. P3 (10/9/2026, piano di
          business): in fase rete /esperienze e' «I prossimi ritiri ed
          esperienze» — tutti i ritiri pubblicati, gratis, con la fascia
          in prima fila; in fase marketplace la directory e' la home. */}
      <Route path="/esperienze" element={<EsperienzeGate />} />
      {/* SC-B2/B3 (27/10/2026): le pagine delle date per mese, regione e stagione */}
      <Route path="/esperienze/mese/:chiave" element={<EsperienzePeriodoPage tipo="mese" />} />
      <Route path="/esperienze/regione/:chiave" element={<EsperienzePeriodoPage tipo="regione" />} />
      <Route path="/esperienze/stagione/:chiave" element={<EsperienzePeriodoPage tipo="stagione" />} />
      <Route path="/esperienze/:categoria" element={<EsperienzeGate />} />
      <Route path="/esperienze/:categoria/:regione" element={<EsperienzeGate />} />
      <Route path="/o/:org_slug" element={<OperatorProfilePage />} />
      {/* LK2 — pagina link per la bio di Instagram: /l/{slug} e' la
          rotta esplicita; /@{slug} vive nel catch-all in fondo (React
          Router non supporta il prefisso @ dentro un segmento) */}
      <Route path="/l/:org_slug" element={<LinkPage />} />
      {/* PV3 — pagina intervista pubblica: stessa testata del profilo,
          redirect a /o/:slug se l'intervista non è pubblicata */}
      <Route path="/o/:org_slug/intervista" element={<OperatorInterviewPage />} />

      <Route path="/e/:org_slug/:slug" element={
        <PublicStorefrontShell showFloatingSwitcher={false}><EventLandingPage /></PublicStorefrontShell>
      } />
      {/* SS (27/10/2026): la landing /p/ di un servizio non esiste piu', il
          profilo fa tutto; i link vecchi arrivano alla riga del listino */}
      <Route path="/p/:org_slug/:product_slug" element={<RimandoLandingServizio />} />
      {/* DP (6/10/2026): la pagina di un prodotto fisico o digitale del profilo,
          da condividere come la landing di un evento; si compra in pagina */}
      <Route path="/prodotto/:org_slug/:slug" element={
        <PublicStorefrontShell showFloatingSwitcher={false}><ProdottoLandingPage /></PublicStorefrontShell>
      } />
      {/* F1 Onda 8: public ticket landing — QR + event details for one holder */}
      <Route path="/t/:token" element={<TicketLandingPage />} />
      {/* P3 Passaporto Ritiri — area personale utente finale (pubblica, noindex) */}
      <Route path="/account/accedi" element={<RedirectPreservingQuery to="/accedi" />} />
      <Route path="/account/verifica" element={<AccountVerifyEmailPage />} />
      <Route path="/account/nuova-password" element={<AccountResetPasswordPage />} />
      <Route path="/account" element={<AccountPage />} />
      {/* AC2 (7/10/2026) — il corso dello studente, con l'account Aurya */}
      <Route path="/account/corsi/:enrollment_id" element={<CorsoStudentePage />} />
      {/* Onda 14: public booking landing — service appointment details + .ics */}
      <Route path="/b/:token" element={<BookingLandingPage />} />
      {/* Onda 16: public reservation landing (rental range + slot) */}
      <Route path="/r/:org_slug/:product_slug" element={
        <PublicStorefrontShell><ReservationLandingPage /></PublicStorefrontShell>
      } />
      {/* Release 2 (Physical) — dedicated public landing for physical products */}
      <Route path="/ph/:org_slug/:product_slug" element={
        <PublicStorefrontShell><PhysicalLandingPage /></PublicStorefrontShell>
      } />
      {/* Release 3 (Digital) — product landing + token-gated download landing */}
      <Route path="/dg/:org_slug/:product_slug" element={
        <PublicStorefrontShell><DigitalLandingPage /></PublicStorefrontShell>
      } />
      <Route path="/d/:access_token" element={<DownloadLandingPage />} />
      {/* Release 4 (Courses) — public landing for video courses */}
      {/* AC3 (7/10/2026): la pagina del corso; /co legacy rimanda */}
      <Route path="/corso/:org_slug/:slug" element={
        <PublicStorefrontShell showFloatingSwitcher={false}><CorsoLandingPage /></PublicStorefrontShell>
      } />
      <Route path="/co/:org_slug/:product_slug" element={<RedirectCoLegacy />} />
      {/* RF (8/10/2026): la directory dei corsi online, con la categoria nel percorso */}
      <Route path="/corsi" element={<CorsiDirectoryPage />} />
      <Route path="/corsi/:categoria" element={<CorsiDirectoryPage />} />
      {/* Onda 16 Fase 5: post-confirmation reservation landing (token-based) */}
      <Route path="/rsv/:token" element={<ReservationConfirmationPage />} />
      {/* Customer Portal legacy — PS4: UN SOLO login utente.
          Restano vive SOLO le rotte strutturali per il player corsi:
            /account/login           target del gate CustomerProtectedRoute
                                     (+ link dai checkout legacy /s/, /p/)
            /account/forgot-password linkata da /account/login e dalle
                                     email di recupero password
            /account/reset-password  link nelle email di reset (JWT customer)
            /account/verify-email    link nelle email di verifica firma
            /account/courses(+/:id)  player corsi: le email "Vai al corso"
                                     puntano a /account/courses/<enrollment_id>
          Tutto il resto del vecchio portale (signup, ordini, profilo)
          redirige alle rotte Aurya: la history acquisti vive in
          /account (platform account), il login unico e' /account/accedi. */}
      {/* CP4 (20/8) — la porta dormiente si spegne. Il portale clienti
          org-scoped non e' linkato da nessuna parte e in produzione
          conta ZERO account e ZERO aule (verificato in sola lettura):
          l'ingresso passa dalla porta unica, conservando ?next=.
          Le pagine di verifica/reset restano vive per gli eventuali
          link gia' spediti. */}
      <Route path="/account/login" element={<RedirectPreservingQuery to="/accedi" />} />
      <Route path="/account/signup" element={<Navigate to="/accedi?vista=crea" replace />} />
      <Route path="/account/forgot-password" element={<CustomerForgotPasswordPage />} />
      <Route path="/account/reset-password" element={<CustomerResetPasswordPage />} />
      <Route path="/account/verify-email" element={<CustomerVerifyEmailPage />} />
      {/* Ordini e profilo legacy → account Aurya (vecchie email
          /account/orders/<id> atterrano sul gate /account, che senza
          token piattaforma rimanda a /account/accedi). */}
      <Route path="/account/orders" element={<Navigate to="/account" replace />} />
      <Route path="/account/orders/:orderId" element={<Navigate to="/account" replace />} />
      <Route path="/account/profile" element={<Navigate to="/account" replace />} />

      {/* ── Player corsi legacy — nested under <CustomerLayout> ───────
          Gated dal JWT customer (CustomerProtectedRoute). Il doppio
          /account che viveva qui (redirect morto a /account/orders,
          oscurato dalla rotta Aurya sopra) e' stato potato in PS4. */}
      <Route path="/account/courses" element={<Navigate to="/account" replace />} />
      <Route path="/account/courses/:enrollment_id" element={<RedirectCorsoLegacy />} />
      {/* ID-bis (20/8) — il vecchio link di registrazione condiviso con
          gli operatori porta alla registrazione NUOVA: la landing
          professionisti con il form incorporato (RD-bis). */}
      <Route path="/signup" element={<Navigate to="/entra-nella-rete#presentati" replace />} />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      {/* Reset-password is intentionally NOT wrapped in PublicRoute so that  */}
      {/* an already-logged-in user can still follow a reset link if needed.  */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Email verification — public, no auth wrapper (user clicks link from email) */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* Onda 28 — blocking page for authenticated-but-unverified users.
          Wrapped by RequireAuthOnly so:
            - unauthenticated users bounce to "/"
            - already-verified or system_admin users bounce to /dashboard
            - authenticated-but-unverified users see the page (and only this page) */}
      <Route
        path="/verify-email-required"
        element={
          <RequireAuthOnly>
            <VerifyEmailRequiredPage />
          </RequireAuthOnly>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      {/* Customer insights — both URLs serve the same component:
            • /modules/customers-light   canonical merchant URL
              (preserved across the legacy cutover; the sidebar menu
              link still points here)
            • /modules/customer-insights alias kept for anyone who
              bookmarked the Phase-2 URL during the short rollout
          The legacy /modules/_legacy/customers-light route was
          removed when the legacy package was deleted. */}
      <Route
        path="/modules/customers-light"
        element={
          <ProtectedRoute>
            <CustomerInsightsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/customer-insights"
        element={
          <ProtectedRoute>
            <CustomerInsightsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/product-catalog"
        element={
          <ProtectedRoute>
            <ProductPerformancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules"
        element={
          <ProtectedRoute>
            <ModulesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={<ProtectedRoute><ProductsPage /></ProtectedRoute>}
      />
      {/* RS1 — Ritiri e' una pagina di ritiri: niente hub multi-tipo.
          ProductsPage resta viva su /products per il commerce legacy. */}
      <Route
        path="/events"
        element={<ProtectedRoute><EventsListPage /></ProtectedRoute>}
      />
      {/* G2: guided event creation wizard (dedicated flow for event_ticket) */}
      <Route
        path="/events/new"
        element={<ProtectedRoute><EventWizard /></ProtectedRoute>}
      />
      {/* E6: unified admin dashboard for one event occurrence */}
      <Route
        path="/events/:occurrence_id"
        element={<ProtectedRoute><EventDashboardPage /></ProtectedRoute>}
      />
      {/* F5 Onda 12: service wizard + dashboard (consulenze, servizi a slot) */}
      <Route
        path="/listino"
        element={<ProtectedRoute><ListinoPage /></ProtectedRoute>}
      />
      {/* P1 (6/10/2026) — Prodotti: lista, wizard digitale, scheda */}
      <Route path="/prodotti" element={<ProtectedRoute><ProdottiGate><ProdottiPage /></ProdottiGate></ProtectedRoute>} />
      <Route path="/prodotti/nuovo/digitale" element={<ProtectedRoute><ProdottiGate><ProdottoDigitaleWizard /></ProdottiGate></ProtectedRoute>} />
      <Route path="/prodotti/nuovo/fisico" element={<ProtectedRoute><ProdottiGate><ProdottoFisicoWizard /></ProdottiGate></ProtectedRoute>} />
      <Route path="/prodotti/:id" element={<ProtectedRoute><ProdottiGate><ProdottoPage /></ProdottiGate></ProtectedRoute>} />
      {/* AC1 (7/10/2026) — l'Accademia: corsi online con moduli e lezioni */}
      <Route path="/accademia" element={<ProtectedRoute><AccademiaGate><AccademiaPage /></AccademiaGate></ProtectedRoute>} />
      <Route path="/accademia/nuovo" element={<ProtectedRoute><AccademiaGate><CorsoWizard /></AccademiaGate></ProtectedRoute>} />
      <Route path="/accademia/:id" element={<ProtectedRoute><AccademiaGate><CorsoPage /></AccademiaGate></ProtectedRoute>} />
      {/* TW1 — la creazione servizi passa dal Listino; il wizard resta
          l'editor AVANZATO su /services/:id */}
      <Route
        path="/services/new"
        element={<Navigate to="/listino" replace />}
      />
      <Route
        path="/services/:product_id"
        element={<ProtectedRoute><ServiceDashboardPage /></ProtectedRoute>}
      />
      {/* Onda 16 — Reservation wizard (unified rental + slot) */}
      <Route
        path="/reservations/new"
        element={<ProtectedRoute><ReservationWizard /></ProtectedRoute>}
      />
      {/* Release 2 (Physical) — dedicated wizard for item_type=physical */}
      <Route
        path="/physicals/new"
        element={<ProtectedRoute><PhysicalWizard /></ProtectedRoute>}
      />
      <Route
        path="/physicals/:product_id"
        element={<ProtectedRoute><PhysicalDashboardPage /></ProtectedRoute>}
      />
      {/* Release 3 (Digital) — wizard + dashboard for item_type=digital */}
      <Route
        path="/digitals/new"
        element={<ProtectedRoute><DigitalWizard /></ProtectedRoute>}
      />
      <Route
        path="/digitals/:product_id"
        element={<ProtectedRoute><DigitalDashboardPage /></ProtectedRoute>}
      />
      {/* Release 4 (Courses) — admin CRUD for video courses */}
      <Route path="/courses" element={<Navigate to="/accademia" replace />} />
      <Route path="/courses/new" element={<Navigate to="/accademia/nuovo" replace />} />
      <Route path="/courses/:course_id" element={<Navigate to="/accademia" replace />} />
      {/* Onda 16 Fase 5 — Admin reservations dashboard (list di IssuedReservation) */}
      <Route
        path="/reservations"
        element={<ProtectedRoute><ReservationsDashboard /></ProtectedRoute>}
      />
      {/* Consolidamento UI prodotti — dashboard del singolo rental/reservation product */}
      <Route
        path="/reservations/:product_id"
        element={<ProtectedRoute><ReservationDashboardPage /></ProtectedRoute>}
      />
      {/* E5: admin door-scanner for an event occurrence */}
      <Route
        path="/events/:occurrence_id/check-in"
        element={<ProtectedRoute><CheckInPage /></ProtectedRoute>}
      />
      {/* G4: ticketing management — resend email, void single, broadcast */}
      <Route
        path="/events/:occurrence_id/tickets"
        element={<ProtectedRoute><TicketsManagementPage /></ProtectedRoute>}
      />
      <Route
        path="/customers"
        element={<ProtectedRoute><CustomersMgmtPage /></ProtectedRoute>}
      />
      <Route
        path="/suppliers"
        element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>}
      />
      <Route
        path="/orders"
        element={<ProtectedRoute><OrdersPage /></ProtectedRoute>}
      />
      <Route
        path="/calendar"
        element={<ProtectedRoute><CalendarPage /></ProtectedRoute>}
      />
      {/* Coupons moved into StoresPage — redirect for bookmarks */}
      <Route path="/coupons" element={<Navigate to="/stores" replace />} />
      <Route
        path="/data-integrity"
        element={<ProtectedRoute><DataIntegrityPage /></ProtectedRoute>}
      />
      {/* PS3 — rotta deprecata: le impostazioni vivono in /settings */}
      <Route path="/store-settings" element={<Navigate to="/settings" replace />} />
      {/* Wave GDPR-Commerce CG-7 — admin DPA (Data Processing Agreement)
          page. Required by GDPR Art. 28 for the platform↔merchant
          relationship. Protected: only the merchant org's admins. */}
      <Route
        path="/settings/legal/dpa"
        element={<ProtectedRoute><DpaPage /></ProtectedRoute>}
      />
      {/* Backward-compat redirect for any stale /setup link (emails,
          bookmarks, previous deploys). The dynamic Setup Wizard now
          lives as a widget on /dashboard — no dedicated page anymore. */}
      <Route
        path="/setup"
        element={<Navigate to="/dashboard" replace />}
      />
      <Route
        path="/stores"
        element={<ProtectedRoute><StoresPage /></ProtectedRoute>}
      />
      {/* BN2 — /newsletter e' del PUBBLICO (landing lettera): il modulo
          newsletter degli operatori vive su /newsletter-forms (il path
          pubblico veniva matchato per primo e oscurava questa pagina) */}
      <Route
        path="/newsletter-forms"
        element={<ProtectedRoute><NewsletterPage /></ProtectedRoute>}
      />
      <Route
        path="/pos/:storeId"
        element={<ProtectedRoute><PosPage /></ProtectedRoute>}
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <TeamPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans"
        element={
          <ProtectedRoute>
            <PlansPage />
          </ProtectedRoute>
        }
      />
      {/* TR6 — la casa dei moduli premium (Sound Studio, e i prossimi) */}
      <Route
        path="/strumenti"
        element={
          <ProtectedRoute>
            <StrumentiPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      {/* O2 — onboarding guidato: la checklist del primo avvio */}
      <Route
        path="/inizia"
        element={
          <ProtectedRoute>
            <IniziaPage />
          </ProtectedRoute>
        }
      />
      {/* FQ3 — vetrina meditazioni (schermo d'invito senza sblocco) */}
      <Route path="/meditazioni" element={<MeditazioniPage />} />
      <Route path="/meditazioni/playlist/:slug" element={<PlaylistPage />} />
      <Route path="/meditazioni/piu" element={<PiuPage />} />
      {/* FQ1 — ascolto pubblico di una traccia pubblicata. Il namespace
          /frequenze/* appartiene PER SEMPRE ai link condivisi in giro:
          il workspace vive altrove (/sound) proprio per non collidere. */}
      <Route path="/frequenze/:slug" element={<PublicFrequencyPage />} />
      {/* LN+SP — Aurya Sound: ogni vista ha il suo URL. Esplora e
          Impara sono PUBBLICHE (biblioteca editoriale: leggi,
          approfondisci, impara); Crea e Le mie tracce restano
          professionali — il cancello vive DENTRO la pagina (redirect
          /login?next=), cosi' la wildcard monta un solo elemento e la
          sessione dell'operatore sopravvive alla navigazione. Le API
          restano org-scoped: la vera frontiera e' il backend. */}
      {/* L3-bis (26/8) — /sound = LA LANDING DI SISTEMA, coi colori
          del sito: il blu comincia entrando (esplora, esperienze,
          lab, meditazioni). La vecchia porta scura (SoundLandingPage)
          e' ritirata dalla rotta; il suo indice vive in /sound/esplora. */}
      <Route path="/sound" element={<SoundHomePage />} />
      {/* FA7 (FARO) — la pagina-scheda della biblioteca: PRIMA del
          catch-all /sound/* o la vista esplora se la mangia */}
      {/* ES0 (8/10/2026): esplora e fondamenta sono pagine proprie; dietro il flag restano le viste del compositore (/sound/*) */}
      {SOUND_ESPLORA_NUOVA && <Route path="/sound/esplora" element={<BibliotecaPage />} />}
      {SOUND_ESPLORA_NUOVA && <Route path="/sound/impara" element={<FondamentaPage />} />}
      {SOUND_ESPLORA_NUOVA && <Route path="/sound/impara/glossario" element={<FondamentaPage />} />}
      <Route path="/sound/esplora/:slug" element={<SchedaBibliotecaPage />} />
      <Route path="/sound/visual" element={<VisualPage />} />
      {/* LAB — PRIMA del catch-all, o FrequenzePage se lo mangia */}
      <Route path="/sound/lab" element={<LabSala />} />
      <Route path="/sound/lab/banco" element={<LabBanco />} />
      <Route path="/sound/lab/orecchio" element={<LabOrecchio />} />
      <Route path="/sound/lab/ritratto" element={<LabRitratto />} />
      <Route path="/sound/lab/meraviglie" element={<LabMeraviglie />} />
      <Route path="/sound/lab/risonanze" element={<LabRisonanze />} />
      {/* LE ESPERIENZE — una presentazione sola, prima del catch-all */}
      <Route path="/sound/calm" element={<EsperienzaPage id="calm" />} />
      <Route path="/sound/ground" element={<EsperienzaPage id="ground" />} />
      <Route path="/sound/respiro" element={<EsperienzaPage id="respiro" />} />
      {/* P3 — il Builder professionale, PRIMA del catch-all (stessa
          trappola del Lab). Il cancello vive dentro la pagina: senza
          il privilegio non si disegna un Builder funzionante, e le API
          rispondono comunque 403. */}
      {/* M-URL (26/8): ogni vista di Professional ha il suo URL —
          la pagina risolve i segmenti da sola (catalogo/:id, percorso/:id,
          registro, protocollo/:id). Sempre PRIMA del catch-all. */}
      {/* L4 — la VENDITA è pubblica e indicizzata; lo strumento
          (/sound/pro) resta noindex. Due indirizzi, due nature. */}
      <Route path="/sound/professional" element={<ProfessionalLanding />} />
      {/* Crea Studio — la via professionale promossa (27/8) */}
      <Route path="/sound/studio" element={<CreaStudioLanding />} />
      {/* TR4 — il link personale del cliente (ciclo TR) */}
      <Route path="/ascolta/:token" element={<AscoltaPage />} />
      <Route path="/sound/pro/*" element={<SoundProPage />} />
      {/* AI2 (9/10/2026) — Crea con l'IA: pagina propria, PRIMA del catch-all */}
      <Route path="/sound/ai" element={<AiCreaPage />} />
      {/* M3 (16/10/2026) — «Genera» non esiste piu': AI Crea fa anche la sola musica. I link vecchi
          (email, segnalibri, memoria) non muoiono: /sound/genera porta ad AI Crea */}
      <Route path="/sound/genera" element={<Navigate to="/sound/ai" replace />} />
      <Route path="/sound/*" element={<FrequenzePage />} />
      {/* legacy: il vecchio indirizzo del workspace continua a funzionare */}
      <Route path="/frequenze" element={<Navigate to="/sound/esplora" replace />} />
      {/* F2.0 — editor del profilo pubblico operatore (/o/:slug) */}
      <Route
        path="/public-profile"
        element={
          <ProtectedRoute>
            <PublicProfilePage />
          </ProtectedRoute>
        }
      />
      {/* PR3 — plancia recensioni operatore */}
      <Route
        path="/reviews"
        element={
          <ProtectedRoute>
            <ReviewsAdminPage />
          </ProtectedRoute>
        }
      />
      {/* VT5 — specchietto visibilita' operatore */}
      <Route
        path="/visibilita"
        element={
          <ProtectedRoute>
            <VisibilityPage />
          </ProtectedRoute>
        }
      />
      {/* CF3 — tesoreria operatore */}
      <Route
        path="/incassi"
        element={
          <ProtectedRoute>
            <IncassiPage />
          </ProtectedRoute>
        }
      />
      {/* CG0 — pagina Dati ripristinata: registro vendite (sync ordini +
          manuale), spese, acquisti, costi fissi */}
      <Route
        path="/modules/cashflow/data/:tab?"
        element={
          <ProtectedRoute>
            <CashflowDataPage />
          </ProtectedRoute>
        }
      />

      {/* System Admin Control Panel — requires role === "system_admin" */}
      <Route
        path="/admin"
        element={
          <SystemAdminRoute>
            <AdminPage />
          </SystemAdminRoute>
        }
      />

      {/* SR fase 0 — pagina PROPRIA del system admin (menu System, sotto Aurya Sound):
          le strutture ricettive per i ritiri, e la scheda di una struttura a pagina intera */}
      <Route
        path="/admin/strutture"
        element={
          <SystemAdminRoute>
            <StrutturePage />
          </SystemAdminRoute>
        }
      />
      <Route
        path="/admin/strutture/:id"
        element={
          <SystemAdminRoute>
            <StrutturaScheda />
          </SystemAdminRoute>
        }
      />

      {/* SA-R — Iscritti al Cerchio, Operatori, Magazine, Tecnico: pagine proprie */}
      <Route path="/admin/cerchio" element={<SystemAdminRoute><CerchioPage /></SystemAdminRoute>} />
      <Route path="/admin/operatori" element={<SystemAdminRoute><OperatoriPage /></SystemAdminRoute>} />
      <Route path="/admin/magazine" element={<SystemAdminRoute><MagazinePage /></SystemAdminRoute>} />
      <Route path="/admin/tecnico" element={<SystemAdminRoute><TecnicoPage /></SystemAdminRoute>} />

      {/* M6 (22/10/2026) — le concessioni del system admin: Crea, Accademia, Studio, crediti */}
      <Route path="/admin/concessioni" element={<SystemAdminRoute><ConcessioniPage /></SystemAdminRoute>} />
      {/* PC3 — pagina SEPARATA (non tab): chi puo' comporre in Aurya Sound */}
      <Route
        path="/admin/sound"
        element={
          <SystemAdminRoute>
            <SoundAccessPage />
          </SystemAdminRoute>
        }
      />

      {/* Catch all — ma prima riconosce gli handle /@slug (LK2):
          e' l'URL che l'operatore mette nella bio di Instagram */}
      <Route path="*" element={<CatchAllOrHandle />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  // DV3 (2/10/2026): le discipline create dalla regia si caricano una volta per sessione
  React.useEffect(() => { caricaDiscipline(); }, []);
  // v6.0: Banner precedence — when BillingStatusBanner (blocking) is visible,
  // suppress the softer ReadOnlyGraceBanner to avoid confusing double banners.
  const [billingBannerVisible, setBillingBannerVisible] = useState(false);
  const handleBillingBannerVisible = useCallback((v) => setBillingBannerVisible(v), []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <SiteConfigProvider>
        <AuthProvider>
          <CustomerAuthProvider>
          <BillingProvider>
            <AiAccessProvider>
              <EntitlementsProvider>
                <BillingStatusBanner onVisible={handleBillingBannerVisible} />
                {!billingBannerVisible && <ReadOnlyGraceBanner />}
                {/* v5.8 / Onda 9.R — QuotaExceededBanner rimosso: era ridondante
                    con <QuotaExceededPaywall />. L'utente percepiva "popup duplicato"
                    + il banner appariva sotto la sidebar (z-index conflict).
                    Il modal paywall e' piu' chiaro, action-oriented, e non ha
                    problemi di z-index.
                <QuotaExceededBanner /> */}
                <QuotaExceededPaywall />
                <ModuleAccessPaywall />
                {/* Wave GDPR-Admin Phase E — blocking re-consent modal.
                    Self-gating: renders nothing unless the authenticated
                    user has consent_needs_refresh=true. Lives inside the
                    AuthProvider tree because it consumes useAuth(). */}
                <ReconsentModal />
                {/* Wave GDPR-Commerce CG-4 — customer-side parallel modal.
                    Self-gating on customer.consent_needs_refresh from
                    /api/customer/me. Mounted inside CustomerAuthProvider
                    because it consumes useCustomerAuth(). Renders
                    nothing in the happy path. */}
                <CustomerReconsentModal />
                {/* Wave GDPR-Admin Phase E — cookie / tracking disclosure.
                    Non-blocking; auto-hides after first acceptance
                    (localStorage afianco_cookie_disclosure_v1). Shown to
                    everyone (public + authenticated), which is what
                    transparency requires under ePrivacy Recital 25. */}
                <CookieConsentBanner />
                <ScrollToTop />
                <AppRoutes />
                <Toaster position="top-right" richColors />
              </EntitlementsProvider>
            </AiAccessProvider>
          </BillingProvider>
          </CustomerAuthProvider>
        </AuthProvider>
        </SiteConfigProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
